import React, { useState, useEffect } from 'react';
import { supabase } from './lib/supabase';
import Layout from './components/Layout';
import LoginForm from './components/Auth/LoginForm';
import CandidateProfile from './components/Candidate/CandidateProfile';
import EvaluatorProfile from './components/Evaluator/EvaluatorProfile';
import EvaluationList from './components/Evaluator/EvaluationList';
import UniversityDashboard from './components/Dashboard/UniversityDashboard';
import AdminDashboard from './components/Admin/AdminDashboard';
import PublicDiscussion from './components/PublicDiscussion';
import NotificationSettings from './components/Settings/NotificationSettings';

type UserType = 'candidate' | 'evaluator' | null;
type CurrentPage = 'candidates' | 'evaluator' | 'dashboard' | 'admin' | 'discussion' | 'settings';

function App() {
  const [userType, setUserType] = useState<UserType>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentPage, setCurrentPage] = useState<CurrentPage>('dashboard');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await determineUserType(session.user.email!);
      }
    } catch (error) {
      console.error('Error checking user:', error);
    } finally {
      setLoading(false);
    }
  };

  const determineUserType = async (email: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: adminData } = await supabase
          .from('admin_users')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        setIsAdmin(!!adminData);
      }

      const { data: candidateData } = await supabase
        .from('candidates')
        .select('*')
        .eq('email', email)
        .maybeSingle();
      if (candidateData) {
        setUserType('candidate');
        setCurrentPage('dashboard');
        return;
      }

      const { data: evaluatorData } = await supabase
        .from('evaluators')
        .select('*')
        .eq('email', email)
        .maybeSingle();
      if (evaluatorData) {
        setUserType('evaluator');
        setCurrentPage('dashboard');
        return;
      }

      await supabase.auth.signOut();
      setUserType(null);
    } catch (error) {
      console.error('Error determining user type:', error);
      setUserType(null);
    }
  };

  const handleLogin = async (type: 'candidate' | 'evaluator' | 'admin') => {
    if (type === 'admin') {
      setUserType('evaluator');
      setIsAdmin(true);
    } else {
      setUserType(type);
    }
    setCurrentPage('dashboard');
  };

  const handleLogout = () => {
    setUserType(null);
    setCurrentPage('dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!userType) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <Layout
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      userType={userType}
      isAdmin={isAdmin}
      onLogout={handleLogout}
    >
      {(() => {
        switch (currentPage) {
          case 'candidates':
            return userType === 'candidate' ? <CandidateProfile /> : <EvaluatorProfile />;
          case 'evaluator':
            return userType === 'evaluator' ? <EvaluatorProfile /> : <CandidateProfile />;
          case 'dashboard':
            return <UniversityDashboard />;
          case 'admin':
            return isAdmin ? <AdminDashboard /> : <UniversityDashboard />;
          case 'discussion':
            return <PublicDiscussion />;
          case 'settings':
            return <NotificationSettings />;
          default:
            return <UniversityDashboard />;
        }
      })()}
    </Layout>
  );
}

export default App;
